import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  BANNER_PRESETS,
  BANNER_ICONS,
  BANNER_ACTIONS,
  BANNER_AUDIENCES,
  BANNER_PLATFORMS,
  BannerActionType,
  BannerAudience,
  BannerPlatform,
  BannerPreset,
  BannerCustomStyle,
  BannerVisualOverride,
} from '../../../api/banners';
import { BannerPreview } from './BannerPreview';
import { BannerColorEditor } from './BannerColorEditor';
import { BannerDialogProps } from '../types';

const LANGS: Array<{ key: 'uz' | 'ru' | 'kiril'; label: string }> = [
  { key: 'uz', label: "O'zbekcha (lotin)" },
  { key: 'ru', label: 'Русский' },
  { key: 'kiril', label: 'Ўзбекча (кирилл)' },
];

export function BannerDialog({
  isDark,
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  title,
  description,
  submitLabel,
  isSubmitting = false,
  errors = {},
  onClearFieldError,
}: BannerDialogProps) {
  const [previewLang, setPreviewLang] = useState<'uz' | 'ru' | 'kiril'>('ru');
  const [showColorEditor, setShowColorEditor] = useState(false);

  const errorInputCls = 'border-red-500 focus-visible:ring-red-500';
  const inputCls = cn(
    isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200',
  );
  const labelCls = cn(isDark ? 'text-slate-300' : 'text-slate-700');

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-w-4xl max-h-[90vh] overflow-y-auto',
          isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white',
        )}
      >
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#111111]')}>
            {title}
          </DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Название — только для админки */}
          <div className="space-y-2">
            <Label className={labelCls}>Название *</Label>
            <Input
              value={formData.name}
              onChange={(e) => {
                onFormDataChange((prev) => ({ ...prev, name: e.target.value }));
                onClearFieldError?.('name');
              }}
              placeholder="Скидка 30% — сентябрь"
              className={cn(inputCls, errors.name && errorInputCls)}
            />
            <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
              Видно только в админке, пользователю не показывается
            </p>
            {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
          </div>

          {/* Тексты по языкам */}
          <div className="space-y-2">
            <Label className={labelCls}>Заголовок *</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {LANGS.map(({ key, label }) => (
                <div key={key} className="space-y-1">
                  <span className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                    {label}
                  </span>
                  <Input
                    value={formData.title?.[key] ?? ''}
                    onChange={(e) => {
                      onFormDataChange((prev) => ({
                        ...prev,
                        title: { ...prev.title, [key]: e.target.value },
                      }));
                      onClearFieldError?.('title');
                    }}
                    className={cn(inputCls, errors.title && errorInputCls)}
                  />
                </div>
              ))}
            </div>
            {errors.title && <p className="text-xs text-red-500">{errors.title}</p>}
          </div>

          <div className="space-y-2">
            <Label className={labelCls}>Подзаголовок</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {LANGS.map(({ key, label }) => (
                <div key={key} className="space-y-1">
                  <span className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                    {label}
                  </span>
                  <Input
                    value={formData.subtitle?.[key] ?? ''}
                    onChange={(e) =>
                      onFormDataChange((prev) => ({
                        ...prev,
                        subtitle: { ...prev.subtitle, [key]: e.target.value },
                      }))
                    }
                    className={inputCls}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Оформление */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelCls}>Оформление *</Label>
              <Select
                value={formData.preset}
                onValueChange={(value: BannerPreset) =>
                  onFormDataChange((prev) => ({ ...prev, preset: value }))
                }
              >
                <SelectTrigger className={inputCls}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BANNER_PRESETS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Иконка *</Label>
              <Select
                value={formData.icon}
                onValueChange={(value: string) =>
                  onFormDataChange((prev) => ({ ...prev, icon: value }))
                }
              >
                <SelectTrigger className={inputCls}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BANNER_ICONS.map((i) => (
                    <SelectItem key={i.value} value={i.value}>
                      {i.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Превью — ровно та вёрстка, что в приложении */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className={labelCls}>Как это будет выглядеть</Label>
              <div className="flex items-center gap-2">
                <span className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                  Язык
                </span>
                <Select value={previewLang} onValueChange={(v: any) => setPreviewLang(v)}>
                  <SelectTrigger className={cn(inputCls, 'h-8 w-[150px]')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGS.map((l) => (
                      <SelectItem key={l.key} value={l.key}>
                        {l.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <span className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                  Светлая тема
                </span>
                <BannerPreview
                  preset={formData.preset}
                  icon={formData.icon}
                  title={formData.title}
                  subtitle={formData.subtitle}
                  showChevron={formData.action_type !== 'none'}
                  custom={formData.custom_style}
                  lang={previewLang}
                  dark={false}
                />
              </div>
              <div className="space-y-1">
                <span className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                  Тёмная тема
                </span>
                <BannerPreview
                  preset={formData.preset}
                  icon={formData.icon}
                  title={formData.title}
                  subtitle={formData.subtitle}
                  showChevron={formData.action_type !== 'none'}
                  custom={formData.custom_style}
                  lang={previewLang}
                  dark
                />
              </div>
            </div>

            {/* Ручные цвета — по теме независимо */}
            <div className="pt-1">
              <button
                type="button"
                onClick={() => setShowColorEditor((v) => !v)}
                className={cn(
                  'text-xs underline underline-offset-2',
                  isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-800',
                )}
              >
                {showColorEditor ? 'Скрыть ручную настройку цветов' : 'Настроить цвета вручную'}
              </button>
            </div>

            {showColorEditor && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <BannerColorEditor
                  isDark={isDark}
                  mode="light"
                  preset={formData.preset}
                  custom={formData.custom_style}
                  onChange={(next) =>
                    onFormDataChange((prev) => ({
                      ...prev,
                      custom_style: mergeCustom(prev.custom_style, 'light', next),
                    }))
                  }
                />
                <BannerColorEditor
                  isDark={isDark}
                  mode="dark"
                  preset={formData.preset}
                  custom={formData.custom_style}
                  onChange={(next) =>
                    onFormDataChange((prev) => ({
                      ...prev,
                      custom_style: mergeCustom(prev.custom_style, 'dark', next),
                    }))
                  }
                />
              </div>
            )}
          </div>

          {/* Действие */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelCls}>Действие по тапу *</Label>
              <Select
                value={formData.action_type}
                onValueChange={(value: BannerActionType) => {
                  onFormDataChange((prev) => ({ ...prev, action_type: value }));
                  onClearFieldError?.('action_payload');
                }}
              >
                <SelectTrigger className={inputCls}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BANNER_ACTIONS.map((a) => (
                    <SelectItem key={a.value} value={a.value}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.action_type === 'external_url' && (
              <div className="space-y-2">
                <Label className={labelCls}>Ссылка *</Label>
                <Input
                  value={formData.action_payload ?? ''}
                  onChange={(e) => {
                    onFormDataChange((prev) => ({ ...prev, action_payload: e.target.value }));
                    onClearFieldError?.('action_payload');
                  }}
                  placeholder="https://t.me/imtihonplus"
                  className={cn(inputCls, errors.action_payload && errorInputCls)}
                />
                {errors.action_payload && (
                  <p className="text-xs text-red-500">{errors.action_payload}</p>
                )}
              </div>
            )}
          </div>

          {/* Таргетинг */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelCls}>Кому показывать</Label>
              <Select
                value={formData.audience}
                onValueChange={(value: BannerAudience) =>
                  onFormDataChange((prev) => ({ ...prev, audience: value }))
                }
              >
                <SelectTrigger className={inputCls}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BANNER_AUDIENCES.map((a) => (
                    <SelectItem key={a.value} value={a.value}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Платформа</Label>
              <Select
                value={formData.platform}
                onValueChange={(value: BannerPlatform) =>
                  onFormDataChange((prev) => ({ ...prev, platform: value }))
                }
              >
                <SelectTrigger className={inputCls}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BANNER_PLATFORMS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                Баннеры про оплату картой на iOS лучше не показывать — правила Apple
              </p>
            </div>
          </div>

          {/* Показ */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className={labelCls}>Приоритет</Label>
              <Input
                type="number"
                value={formData.priority ?? 0}
                onChange={(e) =>
                  onFormDataChange((prev) => ({ ...prev, priority: Number(e.target.value) }))
                }
                className={inputCls}
              />
              <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                Больше — важнее. Показывается один баннер
              </p>
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Начало показа</Label>
              <Input
                type="datetime-local"
                value={formData.starts_at ?? ''}
                onChange={(e) =>
                  onFormDataChange((prev) => ({ ...prev, starts_at: e.target.value || null }))
                }
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Конец показа</Label>
              <Input
                type="datetime-local"
                value={formData.ends_at ?? ''}
                onChange={(e) => {
                  onFormDataChange((prev) => ({ ...prev, ends_at: e.target.value || null }));
                  onClearFieldError?.('ends_at');
                }}
                className={cn(inputCls, errors.ends_at && errorInputCls)}
              />
              {errors.ends_at && <p className="text-xs text-red-500">{errors.ends_at}</p>}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={formData.is_active ?? true}
              onCheckedChange={(checked) =>
                onFormDataChange((prev) => ({ ...prev, is_active: checked }))
              }
            />
            <Label className={labelCls}>Активен</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isSubmitting}
            className="bg-[#C9A227] hover:bg-[#C9A227]/90"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Замена цветов одной темы. Если обе темы очищены — custom_style становится
 * null, чтобы в базе не оставался пустой объект.
 */
function mergeCustom(
  current: BannerCustomStyle | null | undefined,
  mode: 'light' | 'dark',
  next: BannerVisualOverride | null,
): BannerCustomStyle | null {
  const merged: BannerCustomStyle = { ...(current ?? {}), [mode]: next };
  if (!merged.light && !merged.dark) return null;
  return merged;
}
